/**
 * "Forms waiting for a member" — the Members page (WhatsApp picture intake,
 * 2026-10-14).
 *
 * A consultation form a STAFF member photographs on WhatsApp is matched to a
 * member by the intake saga. When no single member matches, the staff member
 * is asked who it is; a form nobody could place waits here, never auto-picked
 * and never auto-created. Each card shows the answers read off the form and
 * its photo, the possible members the matcher found, and a member search —
 * the same debounced `lookupOptions("members", q)` the transfer modal uses.
 * Seeing the photo and the answers before pressing Save IS the confirmation,
 * which is why both buttons live here and not in a chat
 * (`confirm_picture_intake` / `discard_picture_intake` are dashboard + CLI
 * only).
 *
 * Renders nothing when no form is waiting, so the Members page looks exactly
 * as it did for a studio that never uses the intake.
 */

import { useCallback, useEffect, useState } from "react";
import {
  api,
  type WaitingPictureIntakeForm,
  type WaitingPictureIntakesResponse,
} from "../lib/api";
import {
  candidateReasonsLabel,
  waitingFormExpiryLabel,
  waitingFormOutcomeMessage,
} from "../lib/picture-intakes";
import { studioFormat } from "../lib/studio-tz";
import { useDispatch } from "../lib/use-dispatch";
import { PictureIntakePhotoButton } from "./PictureIntakePhoto";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/** What the two actions answer (`picture-intake-waiting-forms.ts`). */
interface WaitingOutcome {
  readonly outcome: string;
  /** `discard_picture_intake` only: whether the photo left Storage just now. */
  readonly photo_removed?: boolean | null;
}

interface Notice {
  readonly id: string;
  readonly ok: boolean;
  readonly text: string;
}

/** The outcomes after which a form has left the waiting list either way. */
const GONE_OUTCOMES = new Set([
  "confirmed",
  "discarded",
  "already_confirmed",
  "already_discarded",
  "not_waiting",
  "expired",
  "closed",
  "not_found",
]);

export function WaitingFormsPanel(): JSX.Element | null {
  const [data, setData] = useState<WaitingPictureIntakesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [notices, setNotices] = useState<ReadonlyArray<Notice>>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.pictureIntakesWaiting();
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const onSettled = useCallback((notice: Notice) => {
    setNotices((n) => [notice, ...n].slice(0, 5));
    setTick((t) => t + 1);
  }, []);

  if (error !== null) {
    return (
      <section className="panel pi-waiting">
        <h2>Forms waiting for a member</h2>
        <p className="action-modal-error">Couldn&apos;t load the waiting forms: {error}</p>
      </section>
    );
  }
  if (data === null) return null;
  if (data.forms.length === 0 && notices.length === 0) return null;

  return (
    <section className="panel pi-waiting" data-waiting-forms>
      <h2>
        Forms waiting for a member ({data.forms.length}
        {data.has_more ? "+" : ""})
      </h2>
      <p className="member-note">
        Consultation forms photographed on WhatsApp by staff that matched no single member.
        Check the photo, choose whose form it is to save its answers to their profile, or
        discard it. A form nobody files is deleted with its photo after 7 days.
      </p>
      {notices.map((n) => (
        <p key={n.id} className={n.ok ? "pi-notice pi-notice-ok" : "pi-notice"}>
          {n.text}
        </p>
      ))}
      <div className="pi-waiting-list">
        {data.forms.map((form) => (
          <WaitingFormCard key={form.id} form={form} onSettled={onSettled} />
        ))}
      </div>
    </section>
  );
}

interface MemberOption {
  readonly value: string;
  readonly label: string;
  readonly sublabel?: string;
}

function WaitingFormCard({
  form,
  onSettled,
}: {
  readonly form: WaitingPictureIntakeForm;
  readonly onSettled: (notice: Notice) => void;
}): JSX.Element {
  const confirm = useDispatch<{ picture_intake_id: string; member_id: string }, WaitingOutcome>(
    "confirm_picture_intake",
  );
  const discard = useDispatch<{ picture_intake_id: string }, WaitingOutcome>(
    "discard_picture_intake",
  );
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ReadonlyArray<MemberOption> | null>(null);
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState<{ readonly id: string; readonly label: string } | null>(
    null,
  );
  const [message, setMessage] = useState<string | null>(null);

  // Debounced member search (name / email / phone), hidden until staff type.
  useEffect(() => {
    const q = query.trim();
    if (q === "" || chosen !== null) {
      setOptions(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const handle = setTimeout(() => {
      void (async () => {
        try {
          const res = await api.lookupOptions("members", q);
          if (!cancelled) setOptions(res.options);
        } catch (e) {
          if (!cancelled) setMessage(`Couldn't search members: ${(e as Error).message}`);
        } finally {
          if (!cancelled) setSearching(false);
        }
      })();
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, chosen]);

  const settle = (
    outcome: string,
    memberLabel: string | null,
    photoRemoved: boolean | null = null,
  ): void => {
    const said = waitingFormOutcomeMessage(outcome, memberLabel, photoRemoved);
    if (GONE_OUTCOMES.has(outcome)) {
      onSettled({ id: `${form.id}:${outcome}`, ok: said.ok, text: said.text });
    } else {
      setMessage(said.text);
    }
  };

  const save = async (): Promise<void> => {
    if (chosen === null) return;
    setMessage(null);
    const r = await confirm.run({ picture_intake_id: form.id, member_id: chosen.id });
    if (!r.ok) {
      setMessage(`Not saved (${r.code}): ${r.message}`);
      return;
    }
    if (r.response.outcome === "member_gone") setChosen(null);
    settle(r.response.outcome, chosen.label);
  };

  const throwAway = async (): Promise<void> => {
    if (!window.confirm("Delete this form and its photo? This can't be undone.")) return;
    setMessage(null);
    const r = await discard.run({ picture_intake_id: form.id });
    if (!r.ok) {
      setMessage(`Not discarded (${r.code}): ${r.message}`);
      return;
    }
    settle(r.response.outcome, null, r.response.photo_removed ?? null);
  };

  const busy = confirm.loading || discard.loading;
  const sender = form.sender_staff_name ?? "a staff member";

  return (
    <article className="pi-waiting-card" data-picture-intake={form.id}>
      <header className="pi-waiting-head">
        <strong>{form.form_name ?? "Name not read"}</strong>
        <span className="dc-muted">
          From {sender} · {fmtDateTime(form.received_at)} ·{" "}
          {waitingFormExpiryLabel(form.expires_at, Date.now())}
        </span>
      </header>

      {form.answers.length > 0 ? (
        <dl className="pi-answers">
          {form.answers.map((a) => (
            <div key={a.key} className="pi-answer">
              <dt>{a.label}</dt>
              <dd>{a.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="dc-muted">No answers could be read off this form.</p>
      )}
      {form.notes_for_review !== null ? (
        <p className="member-note">Reader&apos;s note: {form.notes_for_review}</p>
      ) : null}
      <p className="pi-photo-row">
        {form.has_photo ? (
          <PictureIntakePhotoButton intakeId={form.id} />
        ) : (
          <span className="pi-photo-gone">The photo has been deleted.</span>
        )}
      </p>

      <div className="pi-choose">
        <span className="pi-choose-label">Whose form is it?</span>
        {chosen !== null ? (
          <div className="pi-chosen">
            <strong>{chosen.label}</strong>
            <button
              type="button"
              className="pi-link-btn"
              disabled={busy}
              onClick={() => {
                setChosen(null);
                setQuery("");
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            {form.candidates.length > 0 ? (
              <div className="pi-candidates">
                {form.candidates.map((c) => (
                  <button
                    key={c.member_id}
                    type="button"
                    className="pi-candidate"
                    onClick={() =>
                      setChosen({ id: c.member_id, label: c.display_name ?? "Unnamed member" })
                    }
                  >
                    {c.display_name ?? "Unnamed member"}
                    {c.reasons.length > 0 ? (
                      <span className="xfer-dest-sub"> · {candidateReasonsLabel(c.reasons)}</span>
                    ) : null}
                  </button>
                ))}
              </div>
            ) : null}
            <input
              type="text"
              className="pi-search"
              placeholder="Name, email or phone…"
              value={query}
              aria-label="Search members by name, email or phone"
              onChange={(e) => setQuery(e.target.value)}
            />
            {searching ? <p className="xfer-hint">Searching…</p> : null}
            {options !== null && options.length > 0 ? (
              <ul className="xfer-dest-list">
                {options.map((o) => (
                  <li key={o.value}>
                    <button
                      type="button"
                      onClick={() => {
                        setChosen({ id: o.value, label: o.label });
                        setOptions(null);
                      }}
                    >
                      {o.label}
                      {o.sublabel ? <span className="xfer-dest-sub"> · {o.sublabel}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {options !== null && options.length === 0 && !searching ? (
              <p className="xfer-hint">No members match. Add the member first, then file the form.</p>
            ) : null}
          </>
        )}
      </div>

      {message !== null ? <p className="action-modal-error">{message}</p> : null}

      <div className="pi-waiting-buttons">
        <button
          type="button"
          className="row-edit-modal-cancel"
          disabled={busy}
          onClick={() => void throwAway()}
        >
          {discard.loading ? "Discarding…" : "Discard"}
        </button>
        <button
          type="button"
          className="xfer-submit"
          disabled={busy || chosen === null}
          onClick={() => void save()}
        >
          {confirm.loading
            ? "Saving…"
            : chosen !== null
              ? `Save to ${chosen.label}`
              : "Choose a member to save"}
        </button>
      </div>
    </article>
  );
}
